import React, { useEffect, useId, useRef } from 'react';
import { cn } from '../../utils/cn';
import { useThemeColors } from '../../providers/ThemeProvider';

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label?: string;
  help?: string;
  error?: boolean;
  onChange?: (checked: boolean) => void;
  indeterminate?: boolean;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ 
    label,
    help,
    error = false,
    disabled,
    onChange,
    className,
    indeterminate = false,
    ...props 
  }, forwardedRef) => {
    const colors = useThemeColors();
    const generatedId = useId();
    const checkboxId = props.id ?? generatedId;
    const internalRef = useRef<HTMLInputElement>(null);
    const ref = forwardedRef || internalRef;

    useEffect(() => {
      const checkbox = typeof ref === 'function' ? null : ref.current;
      if (checkbox) {
        checkbox.indeterminate = indeterminate;
      }
    }, [indeterminate, ref]);
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onChange?.(e.target.checked);
    };

    const checkbox = (
      <input
        ref={ref}
        type="checkbox"
        id={checkboxId}
        disabled={disabled}
        onChange={handleChange}
        className={cn(
          'h-4 w-4 rounded',
          colors.classes.borderDefault,
          colors.classes.textPrimary,
          colors.classes.focusRingPrimary,
          colors.classes.bgPrimary,
          error && colors.classes.borderError,
          disabled === true && 'cursor-not-allowed opacity-50',
          className
        )}
        {...props}
      />
    );

    const hasLabel = label != null && label !== '';
    const hasHelp = help != null && help !== '';

    if (!hasLabel && !hasHelp) {
      return checkbox;
    }

    return (
      <div className="relative flex items-start">
        <div className="flex h-5 items-center">
          {checkbox}
        </div>
        {(hasLabel || hasHelp) && (
          <div className="ml-3 text-sm">
            {hasLabel && (
              <label
                htmlFor={checkboxId}
                className={cn(
                  'font-medium cursor-pointer',
                  error ? colors.classes.textDanger : colors.classes.textPrimary,
                  disabled === true && 'cursor-not-allowed opacity-50'
                )}
              >
                {label}
              </label>
            )}
            {hasHelp && (
              <p className={cn(
                'text-sm',
                error ? colors.classes.textDanger : colors.classes.textSecondary
              )}>
                {help}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';